import React, {useCallback, useMemo} from "react"
import {useSelector} from "react-redux"
import {AppPaths} from "../../lib/routing"
import {GreetingsModal} from "scholastic-client-components/src/modals/GreetingsModal/GreetingsModalLazy"
import {
    BodyTemplate,
    FooterTemplate,
    GreetingsTemplate,
    HeaderTemplate,
} from "scholastic-client-components/src/modals/GreetingsModal/greetingsTemplates"
import {linkify} from "scholastic-client-components/src/utilities/linkify"
import {homeSelectors} from "../../slices/homeSlice"
import {useHistory} from "react-router-dom"
import {useTranslation} from "react-i18next"
import {useDateTimeFormats} from "scholastic-client-components/src/hooks/useDateTimeFormats"
import {moment} from "scholastic-client-components/src/SharedPackages/moment"
import {userGuideSelectors} from "scholastic-client-components/src/slices/userGuideSlice"
import {useMarkNewsAsRead} from "./hooks/useMarkNewsAsRead"
import styles from "./styles.module.scss"

export const NewsModal: React.FC = () => {
    const {t} = useTranslation(["home_portal"])
    const history = useHistory()
    const {dateFormat} = useDateTimeFormats()
    const {fetch} = useMarkNewsAsRead()

    const allNews = useSelector(homeSelectors.getData)?.news
    const unreadNews = useSelector(homeSelectors.getData)?.unreadNews
    const greetingsModalOpen = useSelector(userGuideSelectors.getGreetingsModalOpen)

    // Get the first unread news item, as news should be displayed in order from the oldest to the most recent
    const currentNews = useMemo(() => {
        const id = unreadNews?.[0]
        return allNews?.find((item) => item.id === id)
    }, [allNews, unreadNews])

    const markNewsAsRead = useCallback(
        (currentNewsId: number) => {
            fetch(currentNewsId)
        },
        [fetch]
    )

    const newsTemplate: GreetingsTemplate | undefined = useMemo(() => {
        if (!currentNews) return undefined

        return {
            Header: () => <HeaderTemplate title={t`home_portal:news_modal_title`} />,
            Body: () => (
                <BodyTemplate
                    title={currentNews?.name}
                    text={
                        <div
                            className={styles.text}
                            dangerouslySetInnerHTML={{__html: linkify(currentNews?.text)}}
                        />
                    }
                    disclaimer={moment(currentNews?.startDate).format(dateFormat.numeric)}
                />
            ),
            FooterActions: () => <FooterTemplate onClose={() => markNewsAsRead(currentNews?.id)} />,
        }
    }, [t, currentNews, dateFormat, markNewsAsRead])

    const bodyMaxHeight = useMemo(() => {
        const windowHeight = window.innerHeight
        const headerHeight = 230
        const headerBodySpacing = 28
        const footerHeight = 88
        const modalPadding = 120

        return windowHeight - modalPadding - headerHeight - headerBodySpacing - footerHeight
    }, [])

    if (!currentNews || greetingsModalOpen) return null

    return (
        <GreetingsModal
            useSelector={useSelector}
            appPaths={Object.values(AppPaths)}
            history={history}
            open={!!currentNews}
            template={newsTemplate}
            onClose={() => markNewsAsRead(currentNews?.id)}
            bodyMaxHeight={bodyMaxHeight}
        />
    )
}
